import { z } from 'zod';
import { RECURRENCE_TYPES, RECURRING_STATUSES } from '@shared/constants';
import {
  calendarDateSchema,
  centsSchema,
  nonEmptyString,
  optionalText,
  uuidSchema,
} from './common';

export const recurrenceTypeSchema = z.enum(RECURRENCE_TYPES);
export const recurringStatusSchema = z.enum(RECURRING_STATUSES);

const recurringBase = z.object({
  customerId: uuidSchema,
  propertyId: uuidSchema,
  serviceId: uuidSchema.optional(),
  name: nonEmptyString,
  status: recurringStatusSchema.optional(),
  startDate: calendarDateSchema,
  endDate: calendarDateSchema.optional(),
  recurrenceType: recurrenceTypeSchema,
  intervalCount: z
    .number({ message: 'Enter an interval' })
    .int({ message: 'Interval must be a whole number' })
    .positive({ message: 'Interval must be at least 1' })
    .default(1),
  dayOfWeek: z
    .number({ message: 'Enter a day of the week' })
    .int({ message: 'Day of week must be a whole number' })
    .min(0, { message: 'Sunday is 0' })
    .max(6, { message: 'Saturday is 6' })
    .optional(),
  priceCents: centsSchema.default(0),
  estimatedDurationMinutes: z
    .number({ message: 'Enter a duration in minutes' })
    .int({ message: 'Duration must be a whole number of minutes' })
    .nonnegative({ message: 'Duration must not be negative' })
    .default(60),
  notes: optionalText,
});

const weeklyRefine = (data: { recurrenceType?: string; dayOfWeek?: number }) => {
  if (data.recurrenceType === 'WEEKLY' || data.recurrenceType === 'BIWEEKLY') {
    return data.dayOfWeek !== undefined;
  }
  return true;
};

const endDateRefine = (data: { startDate?: string; endDate?: string }) => {
  if (data.endDate && data.startDate) {
    return data.endDate >= data.startDate;
  }
  return true;
};

export const createRecurringServiceSchema = recurringBase
  .refine(weeklyRefine, {
    message: 'Pick a day of the week for weekly or biweekly schedules',
    path: ['dayOfWeek'],
  })
  .refine(endDateRefine, {
    message: 'End date cannot be before the start date',
    path: ['endDate'],
  });

export const updateRecurringServiceSchema = recurringBase
  .partial()
  .refine((data) => Object.keys(data).length > 0, { message: 'Nothing to update' })
  .refine(weeklyRefine, {
    message: 'Pick a day of the week for weekly or biweekly schedules',
    path: ['dayOfWeek'],
  })
  .refine(endDateRefine, {
    message: 'End date cannot be before the start date',
    path: ['endDate'],
  });

export type RecurrenceType = z.infer<typeof recurrenceTypeSchema>;
export type RecurringStatus = z.infer<typeof recurringStatusSchema>;
export type CreateRecurringServiceInput = z.infer<typeof createRecurringServiceSchema>;
export type UpdateRecurringServiceInput = z.infer<typeof updateRecurringServiceSchema>;
